import { useState, useEffect, useRef } from 'react';

/**
 * Custom Hook für dynamische Lade-Status-Nachrichten
 * Wechselt alle 5-10 Sekunden zwischen seriösen, informativen Nachrichten
 * Falls customMessage übergeben wird (z.B. vom Backend), wird diese bevorzugt angezeigt
 */
export const useLoadingStatus = (isActive = true, customMessage = null) => {
  // Erweiterte seriöse, professionelle Status-Nachrichten
  const statusMessages = [
    "Analysiere Anfrage",
    "Verarbeite Eingabe",
    "Durchsuche Wissensbasis",
    "Suche in AI Search",
    "Frage Agenten an",
    "Suche relevante Informationen",
    "Werte Daten aus",
    "Prüfe Kontext",
    "Verknüpfe Informationen",
    "Konsultiere Wissensquellen",
    "Durchsuche Dokumente",
    "Bereite Antwort vor",
    "Prüfe Quellen",
    "Validiere Ergebnisse",
    "Strukturiere Antwort",
    "Verarbeite Agent-Antwort",
    "Kombiniere Ergebnisse",
    "Optimiere Formulierung",
    "Finalisiere Ausgabe",
    "Gleich fertig",
    "Fast geschafft"
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isActive) {
      setCurrentIndex(0);
      setIsTransitioning(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Zufälliges Intervall zwischen 5 und 10 Sekunden
    const getRandomInterval = () => Math.floor(Math.random() * 5000) + 5000;

    const updateMessage = () => {
      // Transition starten
      setIsTransitioning(true);

      // Nach kurzer Verzögerung neuen Text setzen
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % statusMessages.length);
        setIsTransitioning(false);
      }, 300);

      // Nächstes Intervall mit zufälliger Dauer setzen
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
      intervalRef.current = setTimeout(() => {
        updateMessage();
      }, getRandomInterval());
    };

    // Erstes Update nach zufälligem Intervall
    intervalRef.current = setTimeout(() => {
      updateMessage();
    }, getRandomInterval());

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive]);

  // Falls eine custom Message vom Backend kommt, diese bevorzugen
  const displayMessage = customMessage || statusMessages[currentIndex];

  return {
    message: displayMessage,
    index: currentIndex,
    total: statusMessages.length,
    isCustom: !!customMessage,
    isTransitioning: customMessage ? false : isTransitioning  // Kein Transition bei Backend-Messages
  };
};

/**
 * Spezifische Nachrichten für Dokument-Verarbeitung
 */
export const useDocumentProcessingStatus = (isActive = true, customMessage = null) => {
  const statusMessages = [
    "Dokument wird analysiert",
    "Lese Dokumentstruktur",
    "Extrahiere Textinhalte",
    "Erkenne Formatierung",
    "Verarbeite Abschnitte",
    "Analysiere Tabellen",
    "Prüfe Metadaten",
    "Bereite AI Search vor",
    "Indexiere für Suche",
    "Erstelle Embeddings",
    "Vektorisiere Inhalte",
    "Indexiere Inhalte",
    "Erstelle Suchindex",
    "Verknüpfe Referenzen",
    "Optimiere Struktur",
    "Generiere Zusammenfassung",
    "Validiere Daten",
    "Speichere in Datenbank",
    "Aktualisiere Index",
    "Speichere Ergebnisse",
    "Finalisiere Verarbeitung",
    "Fast abgeschlossen"
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isActive) {
      setCurrentIndex(0);
      setIsTransitioning(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const getRandomInterval = () => Math.floor(Math.random() * 5000) + 5000;

    const updateMessage = () => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % statusMessages.length);
        setIsTransitioning(false);
      }, 300);

      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
      intervalRef.current = setTimeout(() => {
        updateMessage();
      }, getRandomInterval());
    };

    intervalRef.current = setTimeout(() => {
      updateMessage();
    }, getRandomInterval());

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive]);

  // Falls eine custom Message vom Backend kommt, diese bevorzugen
  const displayMessage = customMessage || statusMessages[currentIndex];

  return {
    message: displayMessage,
    index: currentIndex,
    total: statusMessages.length,
    isCustom: !!customMessage,
    isTransitioning
  };
};

/**
 * Spezifische Nachrichten für Agenten-Laden (App-Menü)
 * Etwas lockerer und witziger, aber noch professionell
 */
/**
 * Spezifische Nachrichten für Audio-Transkription (Report Roby)
 * Passend für Audio-Auswertung statt allgemeiner Analyse
 */
export const useAudioLoadingStatus = (isActive = true) => {
  const statusMessages = [
    "Höre zu",
    "Werte Audio aus",
    "Transkribiere Aufnahme",
    "Analysiere Sprache",
    "Verarbeite Aufnahme",
    "Erkenne Inhalte",
    "Extrahiere Informationen",
    "Erstelle Transkript",
    "Verarbeite Sprachdaten",
    "Bereite Antwort vor",
    "Fast fertig"
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isActive) {
      setCurrentIndex(0);
      setIsTransitioning(false);
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const getRandomInterval = () => Math.floor(Math.random() * 4000) + 3000;

    const updateMessage = () => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % statusMessages.length);
        setIsTransitioning(false);
      }, 300);

      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
      intervalRef.current = setTimeout(() => {
        updateMessage();
      }, getRandomInterval());
    };

    intervalRef.current = setTimeout(() => {
      updateMessage();
    }, getRandomInterval());

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive]);

  return {
    message: statusMessages[currentIndex],
    index: currentIndex,
    total: statusMessages.length,
    isTransitioning
  };
};

export const useAgentLoadingStatus = (isActive = true) => {
  const statusMessages = [
    "Agenten werden geweckt",
    "Kaffee wird gekocht",
    "Systeme hochfahren",
    "Agenten melden sich bereit",
    "Verbindung wird hergestellt",
    "Wissen wird geladen",
    "Noch einen Moment",
    "Gleich kann es losgehen"
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isActive) {
      setCurrentIndex(0);
      setIsTransitioning(false);
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Kürzeres Intervall für schnelleres Laden: 2-4 Sekunden
    const getRandomInterval = () => Math.floor(Math.random() * 2000) + 2000;

    const updateMessage = () => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % statusMessages.length);
        setIsTransitioning(false);
      }, 250);

      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
      intervalRef.current = setTimeout(() => {
        updateMessage();
      }, getRandomInterval());
    };

    intervalRef.current = setTimeout(() => {
      updateMessage();
    }, getRandomInterval());

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive]);

  return {
    message: statusMessages[currentIndex],
    index: currentIndex,
    total: statusMessages.length,
    isTransitioning
  };
};

export default useLoadingStatus;
